import React, { useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import { cn } from "../../../libs/utils";
import ActionButton from "../ActionButton";
import { Button } from "../button";
import { Card } from "../Card";

const getCustomStyles = (compact = false) => ({
  table: {
    style: {
      backgroundColor: "#ffffff",
      borderRadius: "0",
      overflow: "hidden",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#f8fafc",
      borderBottomColor: "#e2e8f0",
      borderBottomStyle: "solid",
      borderBottomWidth: "1px",
      fontWeight: 600,
      fontSize: compact ? "13px" : "15px",
      color: "#111827",
      minHeight: compact ? "42px" : "48px",
    },
  },
  rows: {
    style: {
      fontSize: compact ? "13px" : "14px",
      minHeight: compact ? "42px" : "52px",
      borderBottomColor: "#f1f5f9",
      borderBottomStyle: "solid",
      borderBottomWidth: "1px",
    },
    highlightOnHoverStyle: {
      backgroundColor: "#f8fafc",
      color: "#111827",
      transitionDuration: "120ms",
      transitionProperty: "background-color",
    },
  },
  headCells: {
    style: {
      color: "#111827",
      paddingLeft: compact ? "12px" : "16px",
      paddingRight: compact ? "12px" : "16px",
    },
  },
  cells: {
    style: {
      color: "#374151",
      paddingLeft: compact ? "12px" : "16px",
      paddingRight: compact ? "12px" : "16px",
      paddingTop: compact ? "8px" : "10px",
      paddingBottom: compact ? "8px" : "10px",
    },
  },
  progress: {
    style: {
      padding: "32px",
    },
  },
});

const clampPage = (value, totalPages) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue) || numberValue < 1) return 1;
  return Math.min(Math.floor(numberValue), totalPages);
};

const getPageItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const visiblePages = new Set([1, totalPages, currentPage]);

  if (currentPage <= 3) {
    [2, 3, 4].forEach((pageNumber) => visiblePages.add(pageNumber));
  } else if (currentPage >= totalPages - 2) {
    [totalPages - 3, totalPages - 2, totalPages - 1].forEach((pageNumber) =>
      visiblePages.add(pageNumber)
    );
  } else {
    visiblePages.add(currentPage - 1);
    visiblePages.add(currentPage + 1);
  }

  const pages = Array.from(visiblePages)
    .filter((pageNumber) => pageNumber >= 1 && pageNumber <= totalPages)
    .sort((a, b) => a - b);

  return pages.reduce((items, pageNumber, index) => {
    const previousPage = pages[index - 1];

    if (previousPage && pageNumber - previousPage > 1) {
      items.push(`ellipsis-${previousPage}-${pageNumber}`);
    }

    items.push(pageNumber);
    return items;
  }, []);
};

const CommonDataTable = ({
  columns,
  data,
  title = "",
  pagination = true,
  paginationServer = false,
  paginationTotalRows = 0,
  onChangePage,
  onChangeRowsPerPage,
  compact = false,
  dense = false,
  progressPending = false,
  noDataComponent = "No records found",
  paginationPerPage,
  paginationRowsPerPageOptions = [10, 25, 50, 100],
  page = 1,
  filteredRowsCount,
  fixedHeader = false,
  fixedHeaderScrollHeight,
  className = "",
}) => {
  const rows = data || [];
  const totalRows = paginationTotalRows || rows.length;
  const matchedRows = filteredRowsCount ?? totalRows;
  const rowsPerPage = paginationPerPage || paginationRowsPerPageOptions[0] || 10;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRows / rowsPerPage)),
    [rowsPerPage, totalRows]
  );
  const currentPage = clampPage(page, totalPages);
  const pageItems = useMemo(
    () => getPageItems(currentPage, totalPages),
    [currentPage, totalPages]
  );

  useEffect(() => {
    if (pagination && currentPage !== page) {
      onChangePage?.(currentPage);
    }
  }, [currentPage, onChangePage, page, pagination]);

  const handleRowsPerPageChange = (event) => {
    const nextLimit = Number(event.target.value);
    onChangeRowsPerPage?.(nextLimit, currentPage);
  };

  return (
    <div className={cn("min-w-0 pb-4 pt-2", className)}>
      <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2 lg:max-w-lg">
        <Card className="px-4 py-3">
          <p className="text-xs font-medium uppercase text-gray-500">
            Searched Records
          </p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{matchedRows}</p>
        </Card>
        <Card className="px-4 py-3">
          <p className="text-xs font-medium uppercase text-gray-500">
            Total Records
          </p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{totalRows}</p>
        </Card>
      </div>

      <Card className="mt-3 overflow-hidden">
        {pagination && (
          <div className="flex flex-col gap-3 border-b border-slate-100 bg-white px-3 py-3 sm:px-4 lg:flex-row lg:items-center lg:justify-between">
            <p className="text-sm font-medium text-slate-600">
              Page {currentPage} of {totalPages}
            </p>

            <div className="grid grid-cols-1 gap-3 min-[520px]:grid-cols-[auto_1fr] min-[520px]:items-center">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <span>Rows</span>
                <select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  className="h-9 rounded-md border border-slate-200 bg-white px-2.5 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {paginationRowsPerPageOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <div className="flex flex-wrap items-center gap-1.5 min-[520px]:justify-end">
                <ActionButton
                  action="previous"
                  iconOnly
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => onChangePage?.(currentPage - 1)}
                />
                <div className="flex flex-wrap items-center gap-1">
                  {pageItems.map((item) =>
                    typeof item === "number" ? (
                      <Button
                        key={item}
                        type="button"
                        size="sm"
                        variant={item === currentPage ? "default" : "outline"}
                        className="h-9 min-w-9 px-2.5"
                        aria-current={item === currentPage ? "page" : undefined}
                        onClick={() => onChangePage?.(item)}
                      >
                        {item}
                      </Button>
                    ) : (
                      <span
                        key={item}
                        className="flex h-9 min-w-7 items-center justify-center text-sm font-medium text-slate-400"
                      >
                        ...
                      </span>
                    )
                  )}
                </div>
                <ActionButton
                  action="next"
                  iconOnly
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => onChangePage?.(currentPage + 1)}
                />
              </div>
            </div>
          </div>
        )}

        {title && (
          <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">
            {title}
          </div>
        )}

        <div className="min-w-0 overflow-x-auto">
          <DataTable
            columns={columns}
            data={rows}
            customStyles={getCustomStyles(compact)}
            highlightOnHover
            pagination={false}
            paginationServer={paginationServer}
            paginationTotalRows={paginationTotalRows}
            paginationPerPage={paginationPerPage}
            paginationRowsPerPageOptions={paginationRowsPerPageOptions}
            onChangePage={onChangePage}
            onChangeRowsPerPage={onChangeRowsPerPage}
            progressPending={progressPending}
            noDataComponent={
              <div className="px-4 py-10 text-center text-sm text-gray-500">
                {noDataComponent}
              </div>
            }
            persistTableHead
            responsive
            dense={dense}
            fixedHeader={fixedHeader}
            fixedHeaderScrollHeight={fixedHeaderScrollHeight}
          />
        </div>
      </Card>
    </div>
  );
};

export default CommonDataTable;
